<template>
    <b-modal
      v-model="data.showModal"
      size="xl"
      button-size="sm"
      modal-class="dropdown-group-editor"
      title-class="w-full"
      footer-class="w-full"
      no-fade
      @show="onShow"
      no-close-on-esc
      no-close-on-backdrop
      scrollable
    >
      <!-- Modal header -->
      <div slot="modal-title">
        <div class="flex w-full justify-between">
          <div class="flex gap-1">
            <div>Edit group</div>
            <div v-if="group">- {{ group.group_name }}</div>
          </div>
        </div>
      </div>

      <!-- Modal content -->
      <div v-if="group">
        <div>
          <table class="table table-google">
            <tbody>
              <tr>
                <td class="row-fit text-nowrap">ID</td>
                <td>{{ group.id | format }}</td>
              </tr>

              <tr>
                <td class="row-fit text-nowrap">Group Key</td>
                <td>{{ group.group_key }}</td>
              </tr>

              <tr>
                <td class="row-fit text-nowrap">Group Name</td>
                <td>
                  <b-form-input
                    v-model="group.group_name"
                    placeholder="Group Name"
                    size="sm"
                    autocomplete="off"
                    trim
                    autofocus
                  ></b-form-input>
                </td>
              </tr>

              <tr>
                <td class="row-fit text-nowrap">Require Short</td>
                <td>
                  <button-select
                    v-model="group.require_short"
                    :options="options.boolean"
                    size="sm"
                  />
                </td>
              </tr>

              <tr>
                <td class="row-fit text-nowrap">Option Sort</td>
                <td>
                  <button-select
                    v-model="group.option_sort"
                    :options="options.option_sort"
                    size="sm"
                  />
                </td>
              </tr>

              <tr>
                <td class="row-fit text-nowrap">Has Purchase</td>
                <td>
                  <button-select
                    v-model="group.has_purchase"
                    :options="options.boolean"
                    size="sm"
                  />
                </td>
              </tr>

              <tr>
                <td class="row-fit text-nowrap">Option Prefix</td>
                <td>
                  <b-form-input
                    v-model="group.option_prefix"
                    placeholder="Option Prefix"
                    size="sm"
                    autocomplete="off"
                  ></b-form-input>
                </td>
              </tr>

              <tr>
                <td class="row-fit text-nowrap">Option Suffix</td>
                <td>
                  <b-form-input
                    v-model="group.option_suffix"
                    placeholder="Option Suffix"
                    size="sm"
                    autocomplete="off"
                  ></b-form-input>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <h4 class="font-bold text-lg">Extra fields</h4>

          <table class="table table-google table-highlight extra-fields">
            <thead>
              <tr>
                <th><!-- drag handle --></th>
                <th>Key</th>
                <th>Title</th>
                <th>Dropdown</th>
                <th class="row-fit"><!-- Actions --></th>
              </tr>
            </thead>
            <draggable
              tag="tbody"
              :list="group.extras"
              handle=".drag-handle"
            >
               <tr v-for="extra in group.extras" :key="extra.guid">
                <td
                  class="drag-handle row-fit"
                >
                  <div class="flex justify-center">
                    <i class="fas fa-bars"></i>
                  </div>
                </td>
                <td>
                  <div v-if="extra.id">
                    {{ extra.extra_key }}
                  </div>
                  <div v-else>
                    <b-form-input
                      v-model="extra.extra_key"
                      placeholder="Key"
                      size="sm"
                      trim
                      autocomplete="off"
                    ></b-form-input>
                  </div>
                </td>
                <td>
                  <b-form-input
                    v-model="extra.extra_title"
                    placeholder="Title"
                    size="sm"
                    trim
                    autocomplete="off"
                  ></b-form-input>
                </td>
                <td class="row-fit">
                  <v-select
                    v-model="extra.dropdown_key"
                    :options="dropdownKeys"
                    appendToBody
                    class="w-56"
                  />
                </td>
                <td class="row-fit">
                  <b-btn
                    size="sm"
                    variant="danger"
                    :disabled="isLoading"
                    @click="deleteExtra(extra)"
                    title="Delete extra"
                  >
                    &times;
                  </b-btn>
                </td>
               </tr>
            </draggable>
          </table>
        </div>
      </div>
      <div v-else>
        <div v-if="isLoading">Loading..</div>
        <div v-else>Error</div>
      </div>

      <!-- Modal footer -->
      <div slot="modal-footer" class="w-full d-flex gap-4 justify-content-between items-center">
        <div class="flex w-full justify-content-between items-center">
          <div class="flex gap-4">
            <b-btn
              size="sm"
              variant="primary"
              :disabled="isLoading || !canSave"
              @click="onSave"
            >
              Save
            </b-btn>

            <b-btn
              size="sm"
              variant="primary"
              :disabled="isLoading"
              @click="createExtra"
            >
              Add new extra
            </b-btn>
          </div>
          <div class="flex gap-8 items-center">
            <div
              v-if="filters.extra_field === 'short' && shortDuplicates.length > 0"
            >
              <span>Duplicates: </span>
              <span class="text-red-500 font-bold">{{ shortDuplicates.join(', ') }}</span>
            </div>

            <div>
              <button-select
                v-model="filters.tab"
                :options="options.tabs"
                size="sm"
              ></button-select>
            </div>

            <div class="flex gap-2">
              <b-btn
                size="sm"
                variant="danger"
                :disabled="isLoading"
                @click="onDelete"
              >
                Delete
              </b-btn>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </template>

<script>
import {
  generateGuid,
  getArrayDuplicates,
  hasWhitespace,
} from '@/helpers';
import draggable from 'vuedraggable';

const ButtonSelect = () => import('@/components/global/ButtonSelect.vue');

export default {
  name: 'SelectEditor',
  components: {
    ButtonSelect,
    draggable,
  },
  props: {
    data: Object,
  },
  computed: {
    dropdownKeys() {
      return Object.keys(this.$store.state.app.dropdowns);
      // const dropdownKeys = Object.keys(this.$store.state.app.dropdowns).map((dropdownKey) => ({
      //   text: dropdownKey, value: dropdownKey,
      // }));
      // return dropdownKeys;
    },
    shortDuplicates() {
      const shorts = this.group?.options.map((option) => option.option_short);
      if (shorts === undefined) return [];
      const duplicates = getArrayDuplicates(shorts);
      return duplicates;
    },
    isLoading() {
      return this.loadingCount > 0;
    },
    isManual() {
      return this.group?.option_sort === false;
    },
    canSave() {
      if (!this.group) return false;
      if (this.group.group_key.length === 0) return false;
      if (hasWhitespace(this.group.group_key)) return false;
      if (this.group.group_name.length === 0) return false;
      return true;
    },
    sendExtras() {
      if (!this.group) {
        return null;
      }
      const sendExtras = this.group.extras.map((extra, index) => ({
        id: extra.id,
        extra_index: index + 1,
        extra_key: extra.extra_key,
        extra_title: extra.extra_title,
        dropdown_key: extra.dropdown_key,
      }));
      return sendExtras;
    },
    sendData() {
      if (!this.group) {
        return null;
      }
      const sendData = {
        ...this.group,
        extras: this.sendExtras,
      };
      return sendData;
    },
  },
  data() {
    return {
      loadingCount: 0,
      filters: {
        tab: 'options',
      },
      options: {
        boolean: [
          { text: 'Yes', value: true },
          { text: 'No', value: false },
        ],
        option_sort: [
          { text: 'Automatic', value: true },
          { text: 'Manual', value: false },
        ],
      },
      group: null,
    };
  },
  methods: {
    fetchGroup() {
      this.loadingCount++;
      this.$http
        .get(`/select/groups/${this.data.groupKey}`)
        .then((res) => {
          res.body.extras.forEach((extra) => {
            extra.guid = generateGuid();
          });
          this.group = res.body;
        })
        .catch((err) => {
          this.$error.toast(`Failed to fetch group: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
        });
    },
    updateGroup() {
      this.loadingCount++;
      this.$http
        .put(`/select/groups/${this.data.groupKey}`)
        .send({ data: this.sendData })
        .then((res) => {
          this.$emit('updated', res.body);
          this.onClose();
        })
        .catch((err) => {
          this.$toast.error(`Failed to update group: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
          this.$store.dispatch('fetchDropdowns');
        });
    },
    deleteGroup() {
      if (!confirm('Do you really wish to delete this group?')) {
        return;
      }
      this.loadingCount++;
      this.$http
        .delete(`/select/groups/${this.data.groupKey}`)
        .then(() => {
          this.$emit('deleted', this.data.groupKey);
          this.onClose();
        })
        .catch((err) => {
          this.$toast.error(`Failed to delete group: ${err.response.text}`);
        })
        .finally(() => {
          this.loadingCount--;
          this.$store.dispatch('fetchDropdowns');
        });
    },
    createExtra() {
      this.group.extras.push({
        id: null,
        guid: generateGuid(),
        extra_key: '',
        extra_title: '',
        extra_index: null,
        dropdown_key: null,
      });
      this.$nextTick().then(() => {
        const inputField = document.querySelector('.modal.dropdown-group-editor .modal-body table.extra-fields tbody > tr:last-child input:first-child');
        if (inputField) {
          inputField.focus();
        }
      });
    },
    deleteExtra(deleteExtra) {
      if (deleteExtra.extra_title.length !== 0 && !confirm(`Do you really wish to delete '${deleteExtra.extra_title}' option?`)) {
        return;
      }
      this.group.extras = this.group.extras.filter((extra) => extra.guid !== deleteExtra.guid);
    },
    fetchAll() {
      this.fetchGroup();
    },
    onSave() {
      this.updateGroup();
    },
    onDelete() {
      this.deleteGroup();
    },
    onShow() {
      this.fetchAll();
    },
    onClose() {
      this.data.showModal = false;
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
</style>
